import { useMemo, useRef, useState, type HTMLAttributes } from "react";

import { AnimatedBackground } from "@components/landing/HeroCard/AnimatedBackground";
import { AnimatedBackgroundMask } from "@components/landing/HeroCard/HeroCard.styled";
import {
  animatedBackgroundGridSize,
  generateOpacityMatrix,
} from "@components/landing/HeroCard/utils";
import { useMediaQueryBreakpoint } from "@hooks";

const width = 1240;
const height = 650;
const columns = width / animatedBackgroundGridSize;
const rows = height / animatedBackgroundGridSize;

export function useHeroAnimatedBackground(): {
  sectionProps: Pick<
    HTMLAttributes<HTMLElement>,
    "onMouseMove" | "onMouseEnter" | "onMouseLeave"
  >;
  background: React.ReactNode;
} {
  const ref = useRef<SVGSVGElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMouseDown, setIsMouseDown] = useState(false);
  const { isTablet } = useMediaQueryBreakpoint();

  const opacityMatrix = useMemo(() => generateOpacityMatrix(columns, rows), []);

  const handleMouseMove = ({
    clientX,
    clientY,
  }: React.MouseEvent<HTMLElement>) => {
    if (!ref.current) return;

    const { x, y } = ref.current.getBoundingClientRect();

    setMousePosition({
      x: clientX - x,
      y: clientY - y,
    });
  };

  const sectionProps = isTablet
    ? {
        onMouseMove: handleMouseMove,
        onMouseEnter: () => setIsMouseDown(true),
        onMouseLeave: () => setIsMouseDown(false),
      }
    : {};

  const background = isTablet ? (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <AnimatedBackgroundMask className="absolute inset-0">
        <AnimatedBackground
          columns={columns}
          rows={rows}
          opacityMatrix={opacityMatrix}
          className="absolute bottom-[-8px] left-0"
          width={width}
          height={height}
        />
        <AnimatedBackground
          ref={ref}
          columns={columns}
          rows={rows}
          opacityMatrix={opacityMatrix}
          gradient
          className="absolute bottom-[-8px] left-0"
          width={width}
          height={height}
          style={{
            opacity: isMouseDown ? "1" : "0",
            maskComposite: "subtract",
            maskImage: `radial-gradient(200px at ${mousePosition.x}px ${mousePosition.y}px, black 30%, transparent 100%)`,
            transition: "opacity 0.3s",
          }}
        />
      </AnimatedBackgroundMask>
    </div>
  ) : null;

  return { sectionProps, background };
}
